import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { catchError, firstValueFrom, map, Observable, of, throwError } from 'rxjs';

import { ERROR_MAP } from '../constants/smart-error-map';
import {
    SmartAnnulRequest,
    SmartApiError,
    SmartApiResponse,
    SmartBackendDifferentialReversalRequest,
    SmartBackendInvoiceRequest,
    SmartBackendNoteRequest,
    SmartBackendResponse,
    SmartDocumentTypeCode,
    SmartImprFiscalPrinter,
    SmartRefreshStatusRequest,
    SmartReportZPayload,
    SmartRetentionRequestDto,
    UnidigitalIntegrationSummary,
} from '../models/smart-api.model';

type FiscalDirectoryPermissionMode = 'read' | 'readwrite';
type FiscalFileSystemPermissionState = 'granted' | 'denied' | 'prompt';

interface FiscalFileSystemHandle {
    queryPermission?(descriptor?: { mode?: FiscalDirectoryPermissionMode }): Promise<FiscalFileSystemPermissionState>;
    requestPermission?(descriptor?: { mode?: FiscalDirectoryPermissionMode }): Promise<FiscalFileSystemPermissionState>;
}

interface FiscalFileSystemWritableFileStream {
    write(data: Blob | string): Promise<void>;
    close(): Promise<void>;
}

interface FiscalFileSystemFileHandle extends FiscalFileSystemHandle {
    createWritable(): Promise<FiscalFileSystemWritableFileStream>;
}

interface FiscalFileSystemDirectoryHandle extends FiscalFileSystemHandle {
    readonly name?: string;
    getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<FiscalFileSystemDirectoryHandle>;
    getFileHandle(name: string, options?: { create?: boolean }): Promise<FiscalFileSystemFileHandle>;
}

interface FiscalWindow extends Window {
    showDirectoryPicker?: (options?: { id?: string; mode?: FiscalDirectoryPermissionMode; startIn?: string }) => Promise<FiscalFileSystemDirectoryHandle>;
}

export type FiscalDirectoryPreparationResult = 'ready' | 'cancelled' | 'unavailable';
export type FiscalDirectoryStatus = 'configured' | 'permission-required' | 'not-configured' | 'unsupported';
export interface FiscalDirectoryState {
    status: FiscalDirectoryStatus;
    hasStoredDirectory: boolean;
    isSupported: boolean;
    suggestedPath: string;
}
export type SmartJsonDownloadResult = 'saved' | 'initiated' | 'failed';

@Injectable({
    providedIn: 'root',
})
export class SmartApiService {
    // URLs del backend (proxy SMART)
    private readonly FACTURACION_URL = `/smart/invoice`;
    private readonly DOWNLOAD_JSON_TOKEN_URL = `/smart/download-json-token`;
    private readonly DOWNLOAD_JSON_URL = `/smart/download-json`;
    private readonly NOTE_URL = `/smart/note`;
    private readonly ANNUL_URL = `/smart/annul`;
    private readonly REFRESH_STATUS_URL = `/smart/refresh-status`;
    private readonly DIFFERENTIAL_NOTE_URL = `/smart/differential-note`;
    private readonly DIFFERENTIAL_PREVIEW_URL = `/smart/differential-preview`;
    private readonly DIFFERENTIAL_REVERSAL_NOTE_URL = `/smart/differential-reversal-note`;
    private readonly DIFFERENTIAL_REVERSAL_PREVIEW_URL = `/smart/differential-reversal-preview`;
    private readonly REPORTEZ_URL = `/smart/reportz`;
    private readonly RETENCION_URL = `/smart/retention`;
    private readonly DOWNLOAD_DB_NAME = 'fina-smart-downloads';
    private readonly DOWNLOAD_STORE_NAME = 'handles';
    private readonly DOWNLOAD_HANDLE_KEY = 'fiscal-directory';
    private readonly SUGGESTED_FISCAL_FOLDERS = ['Facturas_Prosales', 'Fina'];
    private preparedBrowserDownloadWindow: Window | null = null;

    constructor(private http: HttpClient) {}

    prepareBrowserDownloadWindow(): void {
        if (typeof window === 'undefined') {
            return;
        }

        if (this.preparedBrowserDownloadWindow && !this.preparedBrowserDownloadWindow.closed) {
            return;
        }

        const targetName = `smart-json-download-window-${Date.now()}`;
        try {
            console.log('[SMART][FS] prepareBrowserDownloadWindow:start', { targetName });
            const preparedWindow = window.open('about:blank', '_blank');
            if (!preparedWindow) {
                console.warn('[SMART][FS] prepareBrowserDownloadWindow:window.open returned null');
                this.preparedBrowserDownloadWindow = null;
                return;
            }

            preparedWindow.name = targetName;
            this.preparedBrowserDownloadWindow = preparedWindow;
        } catch (error) {
            console.warn('[SMART][FS] No se pudo preparar ventana de descarga.', error);
            this.preparedBrowserDownloadWindow = null;
        }
    }

    public isLikelyImprFiscalJsonContent(response: any): boolean {
        if (!response || typeof response !== 'object') {
            return false;
        }

        const raw = response?.data ?? response;
        const hasTransactionId = typeof raw?.transactionId === 'string' && raw.transactionId.trim().length > 0;
        const hasTipo = typeof raw?.tipo === 'string' && raw.tipo.trim().length > 0;
        const hasProductos = Array.isArray(raw?.productos) && raw.productos.length > 0;
        const hasPdfUrl = !!(raw?.pdfUrl || raw?.url || raw?.urlpdf || raw?.resp?.url || raw?.resp?.urlpdf);

        return (hasTransactionId || hasTipo || hasProductos) && !hasPdfUrl;
    }

    discardPreparedBrowserDownloadWindow(): void {
        if (!this.preparedBrowserDownloadWindow || this.preparedBrowserDownloadWindow.closed) {
            this.preparedBrowserDownloadWindow = null;
            return;
        }

        try {
            this.preparedBrowserDownloadWindow.close();
        } catch (error) {
            console.warn('[SMART][FS] No se pudo cerrar la ventana de descarga preparada.', error);
        } finally {
            this.preparedBrowserDownloadWindow = null;
        }
    }

    hasPreparedBrowserDownloadWindow(): boolean {
        return !!this.preparedBrowserDownloadWindow && !this.preparedBrowserDownloadWindow.closed;
    }

    /**
     * Envío minimal de factura (billId/tenantId) al backend.
     */
    sendFiscalDocument(dto: SmartBackendInvoiceRequest): Observable<SmartBackendResponse> {
        return this.sendInvoiceMinimal(dto);
    }

    /**
     * Envío minimal de nota (NC/ND) al backend.
     */
    sendNote(dto: SmartBackendNoteRequest): Observable<SmartBackendResponse> {
        return this.sendNoteMinimal(dto);
    }

    /**
     * Integración SMART del tenant actual (JWT). Sin token en respuesta.
     * 404 si no hay fila en SmartIntegration.
     */
    getSmartIntegration(): Observable<{
        success: boolean;
        data?: {
            scope?: string;
            hasToken?: boolean;
            rif?: string;
            tenantId?: string;
            isProduction?: boolean;
            /** 'SMART' (default) o 'UNIDIGITAL' para el modo digital. */
            provider?: 'SMART' | 'UNIDIGITAL';
            unidigital?: UnidigitalIntegrationSummary | null;
            imprFiscalPrinters?: SmartImprFiscalPrinter[];
            series?: string[];
            useSerialization?: boolean;
            /** FINA-3330: contribuyente especial (agente de percepción del IGTF). Lo fija soporte en el api. */
            isSpecialTaxpayer?: boolean;
            /** FINA-3330: política derivada por el api (`!hasIntegration || isSpecialTaxpayer`). */
            igtfAllowed?: boolean;
            lastControlNumber?: string | null;
            suggestedControlNumber?: string;
            lastCreditNoteControlNumber?: string | null;
            suggestedCreditNoteControlNumber?: string;
            lastDebitNoteControlNumber?: string | null;
            suggestedDebitNoteControlNumber?: string;
        };
    } | null> {
        const url = `${environment.API_URL}/smart/integration`;
        return this.http.get<any>(url).pipe(catchError(() => of(null)));
    }

    /**
     * Anula un documento UniDigital (requiere número de control ya asignado; en la ventana de
     * ~5-7 min posterior a la emisión el backend responde 422 con mensaje accionable).
     */
    annulFiscalDocument(dto: SmartAnnulRequest): Observable<{ success: boolean; data?: any; message?: string }> {
        if (!dto?.billId || !dto?.tenantId) {
            return throwError(() => ({ message: 'Faltan billId o tenantId' }));
        }
        return this.http.post<any>(`${environment.API_URL}${this.ANNUL_URL}`, dto).pipe(catchError(err => throwError(() => this.mapError(err))));
    }

    /**
     * Fuerza una pasada de reconciliación del estado fiscal de la bill (número de control,
     * PDF, sync Cashea). Devuelve el bloque `fiscal` actualizado de la bill.
     */
    refreshFiscalStatus(dto: SmartRefreshStatusRequest): Observable<{ success: boolean; data?: { fiscal?: any } }> {
        if (!dto?.billId || !dto?.tenantId) {
            return throwError(() => ({ message: 'Faltan billId o tenantId' }));
        }
        return this.http.post<any>(`${environment.API_URL}${this.REFRESH_STATUS_URL}`, dto).pipe(catchError(err => throwError(() => this.mapError(err))));
    }

    /**
     * Envía un reporte Z a SMART vía backend.
     */
    sendReportZ(payload: SmartReportZPayload): Observable<SmartApiResponse> {
        const url = `${environment.API_URL}${this.REPORTEZ_URL}`;

        return this.http.post<any>(url, payload).pipe(
            map(res => this.mapBackendSmartResponse(res)),
            catchError(err => throwError(() => this.mapError(err))),
        );
    }

    /**
     * Envía retenciones (ISLR/IVA) al backend → SMART (`/api/smart/retention`).
     */
    sendRetention(dto: SmartRetentionRequestDto) {
        const url = `${environment.API_URL}${this.RETENCION_URL}`;

        return this.http.post<any>(url, dto).pipe(
            map(res => this.mapBackendSmartResponse(res)),
            catchError(err => throwError(() => this.mapError(err))),
        );
    }

    /** Respuesta `{ success, data, message, smartCode }` del proxy backend. */
    private mapBackendSmartResponse(res: any): SmartApiResponse {
        const data = res?.data ?? res;
        return {
            numeroControl: data?.numeroControl || data?.numControl || data?.controlNumber || null,
            numeroInterno: data?.numeroInterno || data?.numero || null,
            fechaRegistro: data?.fechaRegistro || data?.createdAt || null,
            raw: res,
        };
    }

    getSuggestedFiscalDirectoryPath(): string {
        return this.SUGGESTED_FISCAL_FOLDERS.join('/');
    }

    isFiscalDirectoryPickerSupported(): boolean {
        if (typeof window === 'undefined') {
            return false;
        }
        const fileWindow = window as FiscalWindow;
        return typeof fileWindow.showDirectoryPicker === 'function';
    }

    async getFiscalDirectoryState(): Promise<FiscalDirectoryState> {
        const isSupported = this.isFiscalDirectoryPickerSupported();
        const suggestedPath = this.getSuggestedFiscalDirectoryPath();
        if (!isSupported) {
            return {
                status: 'unsupported',
                hasStoredDirectory: false,
                isSupported,
                suggestedPath,
            };
        }

        const storedHandle = await this.getStoredFiscalDirectoryHandle();
        if (!storedHandle) {
            return {
                status: 'not-configured',
                hasStoredDirectory: false,
                isSupported,
                suggestedPath,
            };
        }

        const permission = await this.queryDirectoryWritePermission(storedHandle);
        return {
            status: permission === 'granted' ? 'configured' : 'permission-required',
            hasStoredDirectory: true,
            isSupported,
            suggestedPath,
        };
    }

    async selectSuggestedFiscalDirectory(): Promise<FiscalDirectoryPreparationResult> {
        return this.prepareSuggestedFiscalDirectory(true);
    }

    async clearSuggestedFiscalDirectory(): Promise<void> {
        if (typeof indexedDB === 'undefined') {
            console.log('[SMART][FS] IndexedDB no disponible para borrar carpeta fiscal.');
            return;
        }

        try {
            const db = await this.openDownloadDatabase();
            await new Promise<void>((resolve, reject) => {
                const transaction = db.transaction(this.DOWNLOAD_STORE_NAME, 'readwrite');
                const store = transaction.objectStore(this.DOWNLOAD_STORE_NAME);
                const request = store.delete(this.DOWNLOAD_HANDLE_KEY);
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            });
            console.log('[SMART][FS] Configuración de carpeta fiscal eliminada.');
        } catch (error) {
            console.warn('No se pudo borrar la carpeta fiscal guardada.', error);
        }
    }

    async prepareSuggestedFiscalDirectory(forceSelection: boolean = false): Promise<FiscalDirectoryPreparationResult> {
        const storedHandle = await this.getStoredFiscalDirectoryHandle();
        if (!forceSelection && storedHandle && (await this.hasDirectoryWritePermission(storedHandle))) {
            console.log('[SMART][FS] Se reutiliza la carpeta fiscal guardada.');
            return 'ready';
        }

        const fileWindow = window as FiscalWindow;
        if (typeof window === 'undefined' || typeof fileWindow.showDirectoryPicker !== 'function') {
            console.log('[SMART][FS] File System Access API no disponible en este navegador.');
            return 'unavailable';
        }

        try {
            console.log('[SMART][FS] Solicitando selección de carpeta fiscal dedicada.');
            const rootHandle = await fileWindow.showDirectoryPicker({
                id: 'fina-fiscal-downloads',
                mode: 'readwrite',
            });
            const targetHandle = await this.ensureFiscalSubdirectories(rootHandle);
            await this.saveStoredFiscalDirectoryHandle(targetHandle);
            console.log('[SMART][FS] Carpeta fiscal preparada y guardada en IndexedDB.');
            return 'ready';
        } catch (error: any) {
            if (error?.name === 'AbortError') {
                console.log('[SMART][FS] El usuario canceló la selección de carpeta fiscal.');
                return 'cancelled';
            }

            console.warn('No se pudo preparar la carpeta sugerida para documentos fiscales. El navegador puede bloquear carpetas del sistema o ubicaciones amplias como Escritorio/Documentos.', error);
            return 'unavailable';
        }
    }

    async downloadSmartJson(response: SmartBackendResponse | any, prefix: 'fac' | 'nc'): Promise<SmartJsonDownloadResult> {
        if (typeof window === 'undefined' || typeof document === 'undefined' || !response) {
            return 'failed';
        }

        const content = response?.data ?? response?.payload;
        console.log('[SMART][FS] downloadSmartJson:start', {
            prefix,
            hasData: !!response?.data,
            hasPayload: !!response?.payload,
            hasContent: !!content,
        });
        if (!content) {
            return 'failed';
        }

        const fileName = this.buildFiscalJsonFileName(prefix);
        const blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json;charset=utf-8' });
        const storedToPreferredDirectory = await this.trySaveSmartJsonToPreferredDirectory(blob, fileName);
        console.log('[SMART][FS] downloadSmartJson:preferredDirectoryResult', { storedToPreferredDirectory, fileName });

        if (storedToPreferredDirectory) {
            this.discardPreparedBrowserDownloadWindow();
            return 'saved';
        }

        return this.triggerBackendJsonDownload(JSON.stringify(content, null, 2), fileName, prefix);
    }

    private async trySaveSmartJsonToPreferredDirectory(blob: Blob, fileName: string): Promise<boolean> {
        const directoryHandle = await this.getStoredFiscalDirectoryHandle();

        if (!directoryHandle) {
            console.log('[SMART][FS] No hay carpeta fiscal guardada. Se usará descarga normal.');
            return false;
        }

        if (!(await this.hasDirectoryWritePermission(directoryHandle))) {
            console.log('[SMART][FS] La carpeta fiscal guardada no tiene permiso de escritura.');
            return false;
        }

        try {
            const fileHandle = await directoryHandle.getFileHandle(fileName, { create: true });
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
            console.log(`[SMART][FS] Archivo fiscal guardado en carpeta persistida: ${fileName}`);
            return true;
        } catch (error) {
            console.warn('No se pudo guardar el JSON fiscal en la carpeta configurada, se usará descarga del navegador.', error);
            return false;
        }
    }

    private async triggerBackendJsonDownload(content: string, fileName: string, prefix: 'fac' | 'nc'): Promise<SmartJsonDownloadResult> {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return 'failed';
        }

        console.log('[SMART][FS] triggerBackendJsonDownload:start', {
            fileName,
            prefix,
            contentLength: content?.length || 0,
            hasPreparedWindow: !!(this.preparedBrowserDownloadWindow && !this.preparedBrowserDownloadWindow.closed),
        });

        if (!this.preparedBrowserDownloadWindow || this.preparedBrowserDownloadWindow.closed) {
            this.prepareBrowserDownloadWindow();
        }
        const preparedWindow = this.preparedBrowserDownloadWindow && !this.preparedBrowserDownloadWindow.closed ? this.preparedBrowserDownloadWindow : null;

        if (!preparedWindow) {
            this.discardPreparedBrowserDownloadWindow();
            return 'failed';
        }

        try {
            const tokenResponse = await firstValueFrom(
                this.http.post<{ success?: boolean; data?: { token?: string } }>(`${environment.API_URL}${this.DOWNLOAD_JSON_TOKEN_URL}`, {
                    prefix,
                    content,
                    suggestedFileName: fileName,
                }),
            );
            const downloadToken = tokenResponse?.data?.token;
            if (!downloadToken) {
                console.warn('[SMART][FS] No se recibió token de descarga del backend.');
                this.discardPreparedBrowserDownloadWindow();
                return 'failed';
            }

            const downloadUrl = `${environment.API_URL}${this.DOWNLOAD_JSON_URL}?token=${encodeURIComponent(downloadToken)}`;
            console.log(`[SMART][FS] Ejecutando descarga por backend: ${fileName}`);
            preparedWindow.location.href = downloadUrl;
            console.log('[SMART][FS] triggerBackendJsonDownload:windowNavigated', {
                downloadUrl,
                target: preparedWindow.name,
            });
            window.setTimeout(() => {
                if (this.preparedBrowserDownloadWindow === preparedWindow) {
                    this.preparedBrowserDownloadWindow = null;
                }
            }, 1000);
            return 'initiated';
        } catch (error) {
            console.warn('[SMART][FS] No se pudo iniciar la descarga por backend.', error);
            this.discardPreparedBrowserDownloadWindow();
            return 'failed';
        }
    }

    /**
     * Ensures Facturas_Prosales/Fina under the selected folder, without re-nesting
     * when the user already picked an intermediate or leaf segment of that path.
     */
    private async ensureFiscalSubdirectories(rootHandle: FiscalFileSystemDirectoryHandle): Promise<FiscalFileSystemDirectoryHandle> {
        const folders = this.SUGGESTED_FISCAL_FOLDERS;
        let startIndex = 0;

        for (let matched = folders.length; matched >= 1; matched -= 1) {
            if (rootHandle?.name === folders[matched - 1]) {
                startIndex = matched;
                break;
            }
        }

        let currentHandle = rootHandle;
        for (let index = startIndex; index < folders.length; index += 1) {
            currentHandle = await currentHandle.getDirectoryHandle(folders[index], { create: true });
        }

        return currentHandle;
    }

    private async hasDirectoryWritePermission(handle: FiscalFileSystemDirectoryHandle): Promise<boolean> {
        if (!handle) {
            return false;
        }

        if ((await handle.queryPermission?.({ mode: 'readwrite' })) === 'granted') {
            return true;
        }

        return (await handle.requestPermission?.({ mode: 'readwrite' })) === 'granted';
    }

    private async queryDirectoryWritePermission(handle: FiscalFileSystemDirectoryHandle): Promise<FiscalFileSystemPermissionState> {
        return (await handle.queryPermission?.({ mode: 'readwrite' })) || 'prompt';
    }

    private async getStoredFiscalDirectoryHandle(): Promise<FiscalFileSystemDirectoryHandle | null> {
        if (typeof indexedDB === 'undefined') {
            console.log('[SMART][FS] IndexedDB no disponible para recuperar carpeta fiscal.');
            return null;
        }

        try {
            const db = await this.openDownloadDatabase();
            return await new Promise<FiscalFileSystemDirectoryHandle | null>((resolve, reject) => {
                const transaction = db.transaction(this.DOWNLOAD_STORE_NAME, 'readonly');
                const store = transaction.objectStore(this.DOWNLOAD_STORE_NAME);
                const request = store.get(this.DOWNLOAD_HANDLE_KEY);
                request.onsuccess = () => {
                    const handle = (request.result as FiscalFileSystemDirectoryHandle) || null;
                    console.log(`[SMART][FS] Recuperación de carpeta fiscal guardada: ${handle ? 'encontrada' : 'no encontrada'}`);
                    resolve(handle);
                };
                request.onerror = () => reject(request.error);
            });
        } catch (error) {
            console.warn('No se pudo leer la carpeta fiscal guardada.', error);
            return null;
        }
    }

    private async saveStoredFiscalDirectoryHandle(handle: FiscalFileSystemDirectoryHandle): Promise<void> {
        if (typeof indexedDB === 'undefined') {
            console.log('[SMART][FS] IndexedDB no disponible para guardar carpeta fiscal.');
            return;
        }

        const db = await this.openDownloadDatabase();

        await new Promise<void>((resolve, reject) => {
            const transaction = db.transaction(this.DOWNLOAD_STORE_NAME, 'readwrite');
            const store = transaction.objectStore(this.DOWNLOAD_STORE_NAME);

            const request = store.put(handle, this.DOWNLOAD_HANDLE_KEY);

            request.onsuccess = () => {
                console.log('[SMART][FS] Carpeta fiscal persistida correctamente.');
                resolve();
            };
            request.onerror = () => reject(request.error);
        });
    }

    private async openDownloadDatabase(): Promise<IDBDatabase> {
        return await new Promise<IDBDatabase>((resolve, reject) => {
            const request = indexedDB.open(this.DOWNLOAD_DB_NAME, 1);

            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(this.DOWNLOAD_STORE_NAME)) {
                    db.createObjectStore(this.DOWNLOAD_STORE_NAME);
                }
            };

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Normaliza la respuesta de SMART.
     *
     * @param res Respuesta cruda del proveedor.
     * @returns `SmartApiResponse` con numeroControl, numeroInterno, fechaRegistro y raw.
     */
    private mapDocumentResponse(res: any): SmartApiResponse {
        const data = res?.data || res;
        return {
            numeroControl: data?.numeroControl || data?.numControl || data?.controlNumber || data?.numeroInterno || data?.control || null,
            numeroInterno: data?.numeroInterno || data?.numero || data?.numInterno || null,
            fechaRegistro: data?.fechaRegistro || data?.createdAt || data?.fecha || null,
            raw: res,
        };
    }

    /**
     * Normaliza errores de SMART a `SmartApiError`.
     *
     * @param error Error recibido en la llamada HTTP.
     * @returns `SmartApiError` con code/message/httpStatus/details.
     */
    private mapError(error: any): SmartApiError {
        const raw = error?.error?.smartCode ?? error?.error?.code ?? error?.error?.codigo ?? error?.error?.error ?? error?.status;
        const code = typeof raw === 'string' && /^\d+$/.test(raw) ? Number(raw) : raw;
        const message = ERROR_MAP[code as keyof typeof ERROR_MAP] || error?.error?.message || error?.message || 'Error desconocido al consumir SMART';
        console.log('error', error);
        console.log('code', code);
        console.log('message', message);
        console.log('httpStatus', error?.status);
        console.log('details', error?.error || error);
        return {
            code,
            message,
            httpStatus: error?.status,
            details: error?.error || error,
        };
    }

    private buildFiscalJsonFileName(prefix: 'fac' | 'nc'): string {
        const now = new Date();
        const yyyy = String(now.getFullYear());
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const hh = String(now.getHours()).padStart(2, '0');
        const mi = String(now.getMinutes()).padStart(2, '0');
        const ss = String(now.getSeconds()).padStart(2, '0');

        return `${prefix}_${yyyy}${mm}${dd}_${hh}${mi}${ss}.json`;
    }

    // ===== BACKEND INTEGRATION =====
    /**
     * Envía factura al backend con payload mínimo (billId/tenantId).
     */
    sendInvoiceMinimal(dto: SmartBackendInvoiceRequest): Observable<SmartBackendResponse> {
        const url = this.FACTURACION_URL;
        if (!dto?.billId || !dto?.tenantId) {
            return throwError(() => ({ message: 'Faltan billId o tenantId' }));
        }
        const body = { ...dto, documentType: dto.documentType ?? SmartDocumentTypeCode.Factura };
        return this.http.post<SmartBackendResponse>(environment.API_URL + url, body);
    }

    /**
     * Envía nota de débito/crédito al backend con payload mínimo.
     */
    sendNoteMinimal(dto: SmartBackendNoteRequest): Observable<SmartBackendResponse> {
        const url = this.NOTE_URL;
        if (!dto?.billId || !dto?.tenantId) {
            return throwError(() => ({ message: 'Faltan billId o tenantId' }));
        }
        if (dto.documentType !== SmartDocumentTypeCode.NotaDebito && dto.documentType !== SmartDocumentTypeCode.NotaCredito) {
            return throwError(() => ({ message: 'documentType debe ser ND (2) o NC (3)' }));
        }
        return this.http.post<SmartBackendResponse>(environment.API_URL + url, dto);
    }

    /**
     * Fase 5 — Emite la nota de diferencial (cambiario + IGTF) de una venta a crédito ya facturada.
     * El backend calcula el diferencial (tipo ND/NC, ítems y montos) desde los datos persistidos; el
     * front solo envía billId/tenantId (+ vendedor fiscal si aplica).
     */
    sendDifferentialNote(dto: SmartBackendInvoiceRequest): Observable<SmartBackendResponse> {
        const url = this.DIFFERENTIAL_NOTE_URL;
        if (!dto?.billId || !dto?.tenantId) {
            return throwError(() => ({ message: 'Faltan billId o tenantId' }));
        }
        return this.http.post<SmartBackendResponse>(environment.API_URL + url, dto);
    }

    /**
     * Fase 5 (Fix 5.3) — Preview (solo lectura) del diferencial cambiario + IGTF de una venta a
     * crédito, para mostrar el monto junto al botón de emitir la nota. No emite nada.
     */
    getDifferentialPreview(dto: SmartBackendInvoiceRequest): Observable<SmartBackendResponse> {
        const url = this.DIFFERENTIAL_PREVIEW_URL;
        if (!dto?.billId || !dto?.tenantId) {
            return throwError(() => ({ message: 'Faltan billId o tenantId' }));
        }
        return this.http.post<SmartBackendResponse>(environment.API_URL + url, dto);
    }

    /**
     * FINA-3326 — Emite la reversa de la nota de diferencial en una devolución TOTAL (una nota por
     * cada documento): el backend construye la nota ESPEJO (tipo opuesto, mismas líneas, tasa de la
     * factura) desde la nota de diferencial persistida y la declara como documento afectado.
     * `dryRun` valida sin emitir; `data.skipped` ⇒ ya no había nada que reversar.
     */
    sendDifferentialReversalNote(dto: SmartBackendDifferentialReversalRequest): Observable<SmartBackendResponse> {
        const url = this.DIFFERENTIAL_REVERSAL_NOTE_URL;
        if (!dto?.billId || !dto?.tenantId) {
            return throwError(() => ({ message: 'Faltan billId o tenantId' }));
        }
        return this.http.post<SmartBackendResponse>(environment.API_URL + url, dto);
    }

    /**
     * FINA-3326 — Preview (solo lectura) de la reversa: `data` es un `DifferentialReversalPreview`
     * (`applies`, tipo espejo, nota afectada, total, rFact, líneas). No emite nada.
     */
    getDifferentialReversalPreview(dto: SmartBackendInvoiceRequest): Observable<SmartBackendResponse> {
        const url = this.DIFFERENTIAL_REVERSAL_PREVIEW_URL;
        if (!dto?.billId || !dto?.tenantId) {
            return throwError(() => ({ message: 'Faltan billId o tenantId' }));
        }
        return this.http.post<SmartBackendResponse>(environment.API_URL + url, dto);
    }
}
